
import { AppNode, Edge } from '../types';

const defaultForType = (type?: string): any => {
  switch (type) {
    case 'number':  return 123;
    case 'boolean': return true;
    case 'date':    return '2025-01-15';
    case 'time':    return '14:30:00';
    case 'datetime': return '2025-01-15T14:30:00';
    case 'email':   return 'user@example.com';
    case 'guid':
    case 'uuid':    return '00000000-0000-0000-0000-000000000000';
    case 'select':  return 'option1';
    case 'checkbox': return true;
    case 'textarea': return 'Sample text...';
    default:        return 'sample';
  }
};

export interface VariableDefinition {
  name: string;
  type: string; // 'string' | 'number' | 'boolean' | 'json' | 'array' | 'unknown'
  sourceNodeId: string;
  sourceNodeLabel: string;
  description?: string;
  example?: any;
}

/**
 * Statically analyzes JavaScript code to find keys in return object literals.
 * Handles:
 * - return { key: val }
 * - return { "key": val }
 * - return { shorthand }
 * - Multiple return statements
 * - Basic nested objects (brace counting)
 */
const analyzeScriptReturns = (script: string): string[] => {
    if (!script || typeof script !== 'string') return [];
    const variables = new Set<string>();

    // 1. Remove comments to avoid false positives
    const cleanScript = script
        .replace(/\/\/.*$/gm, '') // Single line
        .replace(/\/\*[\s\S]*?\*\//g, ''); // Multi line

    // 2. Find locations of "return"
    const returnIndices = [...cleanScript.matchAll(/return\s*\{/g)].map(m => m.index!);

    for (const startIndex of returnIndices) {
        // We found "return {", now we need to find the matching closing "}"
        // The regex match places us at "r" of return.
        // We need to advance to the first "{"
        let braceLevel = 0;
        let objectContent = "";
        let foundStart = false;
        let i = startIndex;

        // Safety break
        while (i < cleanScript.length && i < startIndex + 2000) {
            const char = cleanScript[i];
            
            if (char === '{') {
                if (!foundStart) foundStart = true;
                braceLevel++;
            } else if (char === '}') {
                braceLevel--;
            }

            if (foundStart) {
                objectContent += char;
                if (braceLevel === 0) break; // Found the closing brace for the return object
            }
            i++;
        }

        // 3. Extract keys from the object literal content
        // This looks for identifiers followed by ':' or shorthand identifiers followed by ',' or '}'
        // Regex explanation:
        // (?:[{,]\s*) -> Look for start of obj or comma separator
        // (?:["']?)   -> Optional quote start
        // ([\w$]+)    -> Capture Variable Name (Group 1)
        // (?:["']?)   -> Optional quote end
        // \s*         -> Whitespace
        // (?::|,|\})  -> Followed by colon (key:val), comma (shorthand), or end brace
        const keyMatches = objectContent.matchAll(/(?:[{,]\s*)(?:["']?)([\w$]+)(?:["']?)\s*(?::|,|\})/g);

        for (const match of keyMatches) {
            if (match[1]) {
                variables.add(match[1]);
            }
        }
    }

    return Array.from(variables);
};

/**
 * Traverses the graph backwards from the target node to find all upstream nodes
 * that contribute variables to the context.
 */
export const getAvailableVariables = (
  nodes: AppNode[],
  edges: Edge[],
  targetNodeId: string
): VariableDefinition[] => {
  // Debug Logging Start
  console.groupCollapsed(`🔍 Variable Extraction for Node: ${targetNodeId}`);
  console.log(`Snapshot: ${nodes.length} nodes, ${edges.length} edges`);

  const variables: VariableDefinition[] = [];
  
  // 0. Add System Variables (Always available)
  // We initialize the array with these to ensure it's never empty if properly called
  const systemVars = [
      { name: 'sys.timestamp', type: 'number', sourceNodeId: 'system', sourceNodeLabel: 'System', description: 'Current execution timestamp (ms)', example: Date.now() },
      { name: 'sys.processId', type: 'string', sourceNodeId: 'system', sourceNodeLabel: 'System', description: 'Process Instance ID', example: 'proc_123' },
      { name: 'sys.nodeId', type: 'string', sourceNodeId: 'system', sourceNodeLabel: 'System', description: 'Current Node ID', example: targetNodeId },
  ];
  
  variables.push(...systemVars);

  // Safety check for empty graph
  if (!nodes || nodes.length === 0) {
      console.warn("Variable extraction called with empty nodes array.");
      console.groupEnd();
      return variables;
  }

  const visited = new Set<string>();
  const queue = [targetNodeId];
  const ancestors = new Set<string>();

  // 1. BFS Backwards to find all ancestors
  // Build a reverse adjacency map for faster lookup
  const reverseAdjacency: Record<string, string[]> = {};
  edges.forEach(edge => {
    if (!reverseAdjacency[edge.target]) reverseAdjacency[edge.target] = [];
    reverseAdjacency[edge.target].push(edge.source);
  });

  console.log('Reverse Adjacency Map:', reverseAdjacency);

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    // Don't process the target node itself for variables (unless it's a loop, but we ignore self-refs for context)
    if (currentId !== targetNodeId) {
        ancestors.add(currentId);
    }

    const parents = reverseAdjacency[currentId] || [];
    if (parents.length > 0) {
        console.log(`Node ${currentId} has parents: ${parents.join(', ')}`);
    }

    parents.forEach(parentId => {
      if (!visited.has(parentId)) {
        visited.add(parentId);
        queue.push(parentId);
      }
    });
  }

  console.log(`Found ${ancestors.size} ancestors:`, Array.from(ancestors));

  // 2. Iterate through all nodes, if they are ancestors, extract variables
  // We iterate through the original nodes array to maintain some semblance of definition order
  nodes.forEach(node => {
    if (!ancestors.has(node.id)) return;

    console.groupCollapsed(`Processing Ancestor: ${node.data.label} (${node.type})`);

    const extract = (name: string, type: string, desc?: string, example?: any) => {
        if (!name) return;
        
        console.log(`   -> Extracted: ${name} (${type})`);

        // Avoid duplicates (simple override strategy)
        const existingIdx = variables.findIndex(v => v.name === name);
        const def: VariableDefinition = {
            name,
            type,
            sourceNodeId: node.id,
            sourceNodeLabel: node.data.label || node.id,
            description: desc,
            example
        };
        
        if (existingIdx >= 0) {
            variables[existingIdx] = def;
        } else {
            variables.push(def);
        }
    };

    // --- Extraction Logic based on Node Type ---

    // 1. Form Fields (Start Events & User Tasks)
    // Form fields are wrapped under "input" namespace at runtime by the interface service
    if (node.data.formSchema?.fields) {
        console.log('   - Checking Form Fields...');
        node.data.formSchema.fields.forEach((field: any) => {
            if (field.key) {
                extract(`input.${field.key}`, field.type || 'string', `Form input from ${field.label}`, defaultForType(field.type));
            }
        });
    }

    // 2. Start Events (Message/Signal Payloads)
    if (node.type === 'MessageStartEvent' && node.data.messagePayload) {
        console.log('   - Checking Message Payload...');
        // Flatten payload keys if possible, or just add the root
        extract('messagePayload', 'json', 'Incoming message body', node.data.messagePayload);
        // If the payload has known keys, add them
        try {
            const payload = typeof node.data.messagePayload === 'string' 
                ? JSON.parse(node.data.messagePayload) 
                : node.data.messagePayload;
                
            if (payload && typeof payload === 'object') {
                Object.keys(payload).forEach(key => {
                    extract(key, typeof payload[key], 'Message Payload Property', payload[key]);
                });
            }
        } catch (e) {}
    }

    // 3. Service Tasks (REST)
    if (node.type === 'ServiceTaskREST' && node.data.resultVariable) {
        console.log('   - Checking REST Result...');
        extract(node.data.resultVariable, 'json', 'API Response', { status: 200, data: {} });
    }

    // 4. Database Tasks
    if (node.type === 'ServiceTaskDB' && node.data.resultVariable) {
        console.log('   - Checking DB Result...');
        extract(node.data.resultVariable, 'array', 'Database Query Result', [{ id: 1 }]);
    }

    // 5. LLM Tasks
    if (node.type === 'ServiceTaskLLM' && node.data.outputVariable) {
        console.log('   - Checking LLM Output...');
        extract(node.data.outputVariable, 'string', 'AI Generated Content', 'Sample AI response...');
    }

    // 6. MCP Tasks
    if (node.type === 'ServiceTaskMCP' && node.data.resultVariable) {
        console.log('   - Checking MCP Result...');
        extract(node.data.resultVariable, 'any', 'MCP Operation Result', { content: 'Sample data' });
    }

    // 7. Decision Tasks (Rules)
    if (node.type === 'ServiceTaskRules') {
        console.log('   - Checking Decision Outputs...');
        
        // Use result variable if set (wraps everything)
        if (node.data.resultVariable) {
            extract(node.data.resultVariable, 'object', 'Decision Result Wrapper', { score: 100 });
        }
        
        // Also extract individual outputs if known (from the UI selection state)
        if (node.data._outputSchema && Array.isArray(node.data._outputSchema)) {
            node.data._outputSchema.forEach((out: any) => {
                extract(out.name, out.type, `Decision Output (${node.data.decisionFile})`);
            });
        }
    }

    // 8. Call Activities
    if (node.type === 'CallActivity' && node.data.resultVariable) {
        extract(node.data.resultVariable, 'object', 'Call Activity Result', {});
    }

    // 9. Sub Processes
    if (node.type === 'SubProcess' && node.data.resultVariable) {
        extract(node.data.resultVariable, 'object', 'Sub Process Result', {});
    }

    // 10. Extraction Rules (Generic for Service Tasks)
    if (node.data.extractionRules && Array.isArray(node.data.extractionRules)) {
        console.log('   - Checking Extraction Rules...');
        node.data.extractionRules.forEach(rule => {
            if (rule.targetVar) {
                extract(rule.targetVar, rule.type || 'auto', `Extracted from ${rule.source}`, 'Extracted Value');
            }
        });
    }

    // 9. Script Tasks
    if (node.type === 'ScriptTask') {
        console.log('   - Checking Script Outputs...');
        
        // 9a. Explicitly defined outputs (Manual Override)
        if (Array.isArray(node.data.outputVariables)) {
            node.data.outputVariables.forEach((varName: string) => {
                extract(varName, 'unknown', 'Explicitly defined script output');
            });
        }

        // 9b. Automatic Analysis (Return statements)
        if (node.data.script) {
            const detectedVars = analyzeScriptReturns(node.data.script);
            detectedVars.forEach(varName => {
                // Don't overwrite explicit ones, but add if new
                const existing = variables.find(v => v.name === varName && v.sourceNodeId === node.id);
                if (!existing) {
                    extract(varName, 'unknown', 'Auto-detected from script return');
                }
            });
        }
    }
    
    console.groupEnd();
  });

  console.log(`Total Variables Found: ${variables.length}`, variables);
  console.groupEnd();

  return variables;
};
